/**
 * AboutPage
 * /about
 *
 * Real content rebuild:
 * - Origin timeline (Agency → Realization → Gap → Solution)
 * - AI-Ready feature matrix with concrete points
 * - "What we believe" section
 * - Real Unsplash team/office image
 * - Real contact emails: support@rooflyzer.pro, sales@rooflyzer.pro
 */

'use client';

import { useReducedMotion, motion } from 'framer-motion';
import { SEOHead } from '@/components/seo/SEOHead';
import { SchemaInjector } from '@/components/seo/SchemaInjector';
import { AnimatedSection, StaggerContainer, StaggerItem } from '@/components/marketing';
import {
  BuildingOffice,
  Lightbulb,
  Target,
  Rocket,
  Brain,
  Envelope,
  MapPin,
  Phone,
  ArrowRight,
  Database,
  GitMerge,
  TrendUp,
  Cpu,
  ShieldCheck,
  CheckCircle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

/* ─── SEO ─────────────────────────────────────────────────────────── */
const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.rooflyzer.pro' },
    { '@type': 'ListItem', position: 2, name: 'About Us', item: 'https://www.rooflyzer.pro/about' },
  ],
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Rooflyzer OS',
  url: 'https://www.rooflyzer.pro',
  logo: 'https://www.rooflyzer.pro/rooflyzer-logo.svg',
  foundingDate: '2023',
  description:
    'Rooflyzer OS is an AI-ready roofing business operating system built by marketers who understand the complete customer journey from first click to final payment.',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@rooflyzer.pro',
    contactType: 'customer support',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '30 N Gould St #55327',
    addressLocality: 'Sheridan',
    addressRegion: 'WY',
    postalCode: '82801',
    addressCountry: 'US',
  },
};

/* ─── Origin timeline data ────────────────────────────────────────── */
const timeline = [
  {
    icon: BuildingOffice,
    label: 'Agency',
    year: '2022',
    color: '#38BDF8',
    body: "We started as Rogue Move Digital Agency, a marketing shop focused on roofing contractors. We ran Google Ads, built landing pages, and tracked lead-to-customer attribution. We got good at generating leads. But we kept watching them vanish once they hit the contractor's CRM.",
  },
  {
    icon: Lightbulb,
    label: 'Realization',
    year: '2023',
    color: '#10B981',
    body: "The real problem wasn't marketing. It was data structure. Every lead's source, every touchpoint, every stalled follow-up was either missing or locked in a field the CRM couldn't act on. We couldn't optimize campaigns because we couldn't close the loop on revenue.",
  },
  {
    icon: Target,
    label: 'Gap',
    year: '2024',
    color: '#6366F1',
    body: 'We tested every major roofing CRM. Each one was built by people who had never run a roofing campaign. They tracked contacts. We needed to track the complete journey: ad click, form submit, estimate, proposal, signature, job, payment, and referral. Nothing existed.',
  },
  {
    icon: Rocket,
    label: 'Solution',
    year: '2025',
    color: '#3B82F6',
    body: 'So we built Rooflyzer OS: a single platform that captures the full customer journey as structured data. Leads, estimates, proposals, jobs, invoices, and marketing automation all share the same data model. No more gaps. No more lost attribution.',
  },
];

/* ─── AI-ready feature matrix ────────────────────────────────────── */
const aiFeatures = [
  {
    icon: Database,
    title: 'Structured data at every step',
    desc: 'Lead source, estimate inputs, proposal tier chosen, e-signature timestamp, job stage, payment status, and follow-up outcome are all stored as queryable records.',
  },
  {
    icon: GitMerge,
    title: 'Attribution from first click to final payment',
    desc: 'Track UTM parameters, form embed IDs, campaign names, and referral sources through the entire lifecycle. Know which channels produce actual revenue, not just leads.',
  },
  {
    icon: TrendUp,
    title: 'Built-in analytics and export',
    desc: 'Dashboards, funnel reports, and CSV exports give you clean data without a data engineer. Use it inside Rooflyzer or feed it into your own BI tools.',
  },
  {
    icon: Cpu,
    title: 'Architecture ready for AI',
    desc: 'Lead scoring and anomaly detection are live today. As AI models mature, they plug into the same structured data: address-only roof measurements, automated follow-up, and predictive job costing.',
  },
];

/* ─── Beliefs ─────────────────────────────────────────────────────── */
const beliefs = [
  {
    icon: ShieldCheck,
    title: 'Your data belongs to you',
    desc: 'Export everything. No lock-in. No hostage clauses. We win by keeping you, not by trapping you.',
  },
  {
    icon: CheckCircle,
    title: 'Software should fit the workflow, not the other way around',
    desc: 'Use QuickBooks for accounting, SumoQuote for quotes, or EagleView for measurements if you want. Rooflyzer connects them instead of replacing them by force.',
  },
  {
    icon: Brain,
    title: 'AI is only as good as the data underneath',
    desc: 'We built the data model first so AI can actually do useful work: prioritize hot leads, flag stalled jobs, and predict which campaigns will close.',
  },
];

/* ─── Reusable motion wrapper ─────────────────────────────────────── */
function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

export const AboutPage = () => {
  const pageTitle = 'About Rooflyzer OS — Built by Marketers, Built for Roofers';
  const pageDescription =
    'Rooflyzer OS was built by a roofing marketing agency that got tired of watching leads die in generic CRMs. Learn how we became an AI-ready operating system.';
  const heroTitle = 'Built by marketers. Built for roofers.';
  const heroSubtitle =
    "We didn't start as software developers. We started as a marketing agency that needed a CRM capable of tracking the complete customer journey. When we couldn't find one, we built it.";

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/about"
        keywords="about rooflyzer, rooflyzer company, roofing software company, AI ready roofing CRM"
        ogImage="/social-card.svg"
        schema={breadcrumbSchema}
      />
      <SchemaInjector schema={organizationSchema} />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 overflow-hidden">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-6">
                <BuildingOffice weight="fill" className="w-4 h-4 text-emerald-400" />
                About Rooflyzer OS
              </div>
              <h1 className="hero-h1 font-bold text-white mb-6">
                {heroTitle.split('Built for roofers')[0]}
                <span className="text-white">Built for roofers.</span>
              </h1>
              <p className="hero-lead text-white/70 mb-8">{heroSubtitle}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
                  >
                    Book a demo
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-lg px-8"
                  >
                    Contact sales
                  </Button>
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative rounded-3xl overflow-hidden glass-panel-elevated">
                <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-[#050507]/60 to-transparent z-10" />
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1600&auto=format&fit=crop"
                  alt="Rooflyzer team collaborating in a modern office"
                  className="w-full h-[420px] object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-6 left-6 z-20">
                  <p className="text-white font-semibold text-sm">
                    Rogue Move Digital Agency became Rooflyzer OS.
                  </p>
                  <p className="text-white/50 text-sm">Built from the field, not the boardroom.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Origin Timeline ────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-4">
              <Rocket weight="fill" className="w-4 h-4 text-emerald-400" />
              Our Story
            </div>
            <h2 className="section-h2 font-bold text-white mb-4">
              From roofing marketing agency to{' '}
              <span className="text-white">complete operating system</span>
            </h2>
            <p className="body-lg text-white/55 max-w-2xl mx-auto">
              Every stage of our business taught us something about what roofers actually need.
            </p>
          </AnimatedSection>

          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-white/10 hidden sm:block" />

            <div className="space-y-12">
              {timeline.map((step, i) => {
                const isRight = i % 2 === 1;
                return (
                  <Reveal key={i} delay={i * 0.1}>
                    <div
                      className={`relative flex flex-col md:flex-row items-start ${isRight ? 'md:flex-row-reverse' : ''}`}
                    >
                      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#050507] border-2 border-emerald-400 z-10 hidden sm:block" />
                      <div className="ml-16 md:ml-0 md:w-[calc(50%_-_2.5rem)] glass-panel-interactive rounded-3xl p-6 sm:p-8 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:-translate-y-1 bg-slate-900/40">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-2xl flex items-center justify-center border border-slate-700 bg-slate-800">
                            <step.icon
                              className="w-6 h-6"
                              weight="fill"
                              style={{ color: step.color }}
                            />
                          </div>
                          <div>
                            <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
                              {step.year}
                            </span>
                            <h3 className="text-xl font-bold text-white">{step.label}</h3>
                          </div>
                        </div>
                        <p className="text-white/70 leading-relaxed">{step.body}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Philosophy ─────────────────────────────────────────────── */}
      <section className="relative py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative z-10 max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-4">
              <Brain weight="fill" className="w-4 h-4 text-emerald-400" />
              AI-Ready, Not Just AI-Enabled
            </div>
            <h2 className="section-h2 font-bold text-white mb-4">
              AI is only as good as the <span className="text-white">data underneath it</span>
            </h2>
            <p className="body-lg text-white/55 max-w-2xl mx-auto">
              Every CRM is adding AI features. The difference is whether the data is clean enough
              for AI to produce useful results.
            </p>
          </AnimatedSection>

          <Reveal className="rounded-3xl p-6 sm:p-8 mb-12 max-w-3xl mx-auto text-center bg-slate-900/60 border border-slate-800">
            <p className="text-lg sm:text-xl text-white/85 leading-relaxed italic">
              &ldquo;AI is nothing but the implementation of the data provided. We capture every
              customer engagement so the model has complete, structured data to work with. After
              export, you have the entire dataset: complete, structured, and ready for any AI tool
              or analysis you need.&rdquo;
            </p>
            <p className="text-white/40 text-sm mt-4">Rooflyzer OS founding team</p>
          </Reveal>

          <StaggerContainer className="grid sm:grid-cols-2 gap-5">
            {aiFeatures.map((feat, i) => (
              <StaggerItem key={i}>
                <div className="glass-panel-interactive rounded-3xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:-translate-y-1 h-full bg-slate-900/40">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                      <feat.icon className="w-5 h-5 text-emerald-400" weight="fill" />
                    </div>
                    <h3 className="section-h3 font-bold text-white">{feat.title}</h3>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">{feat.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── What We Believe ─────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-4">
              <ShieldCheck weight="fill" className="w-4 h-4 text-emerald-400" />
              What We Believe
            </div>
            <h2 className="section-h2 font-bold text-white mb-4">
              Principles we built the product on
            </h2>
            <p className="body-lg text-white/55 max-w-2xl mx-auto">
              These are not taglines. They are the decisions we make every time we add a feature or
              change a price.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-3 gap-6">
            {beliefs.map((b, i) => (
              <StaggerItem key={i}>
                <div className="glass-panel-interactive rounded-3xl p-8 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:-translate-y-1 h-full bg-slate-900/40">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-5">
                    <b.icon className="w-7 h-7 text-cyan-400" weight="fill" />
                  </div>
                  <h3 className="section-h3 font-bold text-white mb-3">{b.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Contact & HQ ───────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="section-h2 font-bold text-white mb-3">Get in touch</h2>
            <p className="body-lg text-white/55">
              We are based in the US and respond within 24 hours.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StaggerItem>
              <div className="glass-panel-interactive rounded-3xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full bg-slate-900/40">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                  <Envelope className="w-5 h-5 text-emerald-400" weight="fill" />
                </div>
                <h3 className="font-bold text-white mb-1">Support</h3>
                <p className="text-white/50 text-sm mb-3">Questions about the product</p>
                <a
                  href="mailto:support@rooflyzer.pro"
                  className="text-emerald-400 text-sm hover:underline font-medium"
                >
                  support@rooflyzer.pro
                </a>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="glass-panel-interactive rounded-3xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full bg-slate-900/40">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                  <Phone className="w-5 h-5 text-emerald-400" weight="fill" />
                </div>
                <h3 className="font-bold text-white mb-1">Sales & Partnerships</h3>
                <p className="text-white/50 text-sm mb-3">Pricing, agencies, and integrations</p>
                <a
                  href="mailto:sales@rooflyzer.pro"
                  className="text-emerald-400 text-sm hover:underline font-medium"
                >
                  sales@rooflyzer.pro
                </a>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="glass-panel-interactive rounded-3xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full bg-slate-900/40">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                  <Phone className="w-5 h-5 text-emerald-400" weight="fill" />
                </div>
                <h3 className="font-bold text-white mb-1">Call us</h3>
                <p className="text-white/50 text-sm mb-3">Mon-Fri, 9am-6pm MT</p>
                <a
                  href="tel:+13702898830"
                  className="text-emerald-400 text-sm hover:underline font-medium"
                >
                  (370) 289-8830
                </a>
              </div>
            </StaggerItem>

            <StaggerItem>
              <div className="glass-panel-interactive rounded-3xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full bg-slate-900/40">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                  <MapPin className="w-5 h-5 text-emerald-400" weight="fill" />
                </div>
                <h3 className="font-bold text-white mb-1">Headquarters</h3>
                <p className="text-white/50 text-sm mb-3">Registered office</p>
                <address className="not-italic text-white/60 text-sm leading-relaxed">
                  30 N Gould St #55327
                  <br />
                  Sheridan, WY 82801
                </address>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────────── */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <div className="glass-panel-elevated rounded-3xl p-10 md:p-12 border border-slate-800 bg-slate-900/60">
              <h2 className="section-h2 font-bold text-white mb-4">
                Ready to see what AI-ready actually means?
              </h2>
              <p className="text-white/55 body-lg mb-8">
                Every data point you capture now makes the platform smarter later. Start free or
                book a demo.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
                  >
                    Book a demo
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-lg px-8"
                  >
                    Contact us
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};
